import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, FileText, AlertTriangle, Mail } from "lucide-react";
import { LEGAL_CONTENT, LEGAL_DISCLAIMER } from "./content";

const LOGO = "https://customer-assets.emergentagent.com/job_admin-dashboard-flux/artifacts/ef1ryefo_The%20Gowda%E2%80%99s%20Group.png";

const ROUTE_MAP = {
  terms: "terms",
  privacy: "privacy",
  refund: "refund",
  services: "services",
};

export default function LegalPage() {
  const { doc } = useParams();
  const key = ROUTE_MAP[doc];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);

  if (!key) return <Navigate to="/" replace />;

  const content = LEGAL_CONTENT[key];
  const otherLinks = Object.entries(LEGAL_CONTENT)
    .filter(([k]) => k !== key)
    .map(([k, v]) => ({ slug: k, title: v.title }));

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid={`legal-page-${doc}`}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0A0A0C]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 lg:px-10 py-4">
          <Link to="/" className="flex items-center gap-3" data-testid="legal-logo-link">
            <img src={LOGO} alt="VeloxAlgoTech LLP" className="w-9 h-9 rounded-full ring-1 ring-[#7DD3FC]/30" />
            <div className="flex flex-col leading-none">
              <span className="text-[10px] font-semibold tracking-[0.28em] text-[#7DD3FC]/80" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                VELOX
              </span>
              <span className="text-sm font-bold tracking-[0.18em] text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
                ALGO TECH
              </span>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-white/10 hover:border-white/30 text-xs text-zinc-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={13} />
            Back to home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 lg:px-10 pt-14 pb-20">
        <div className="flex items-center gap-2 mb-5">
          <FileText size={14} className="text-[#7DD3FC]" />
          <span className="text-[10px] tracking-[0.28em] text-[#7DD3FC]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            LEGAL · {content.title.toUpperCase()}
          </span>
        </div>

        <h1
          className="text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.02] text-white mb-3"
          style={{ fontFamily: "'Outfit', sans-serif" }}
          data-testid="legal-title"
        >
          {content.title}
        </h1>
        <p className="text-xs text-zinc-500 mb-10" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          LAST UPDATED — {content.lastUpdated.toUpperCase()}
        </p>

        <p
          className="text-base text-zinc-300 leading-relaxed mb-10"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          data-testid="legal-intro"
        >
          {content.intro}
        </p>

        <div className="space-y-9">
          {content.sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${i}`}>
              <h2
                className="text-xl md:text-2xl font-bold text-white mb-3"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {s.heading}
              </h2>
              <p
                className="text-[15px] text-zinc-400 leading-relaxed whitespace-pre-line"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {s.body}
              </p>
            </section>
          ))}
        </div>

        {/* Risk disclaimer box (shown on every legal page) */}
        <div
          className="mt-14 p-6 rounded-2xl border border-amber-500/20 bg-amber-500/[0.04]"
          data-testid="legal-disclaimer"
        >
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <h3 className="text-sm font-bold text-amber-300" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {LEGAL_DISCLAIMER.title}
            </h3>
          </div>
          <ul className="space-y-2 pl-7 text-[13px] text-amber-200/75 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {LEGAL_DISCLAIMER.body.map((line, i) => (
              <li key={i} className="list-disc">
                {line}
              </li>
            ))}
          </ul>
        </div>

        {/* Contact + other docs */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          <a
            href="mailto:support@veloxalgotech.com"
            data-testid="legal-support-link"
            className="p-5 rounded-2xl border border-white/10 hover:border-[#7DD3FC]/40 bg-[#13131A]/60 transition-colors flex items-center gap-4"
          >
            <div className="w-11 h-11 rounded-xl bg-[#7DD3FC]/10 border border-[#7DD3FC]/20 flex items-center justify-center">
              <Mail size={18} className="text-[#7DD3FC]" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] text-zinc-500 mb-0.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                QUESTIONS?
              </div>
              <div className="text-sm text-white font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>
                support@veloxalgotech.com
              </div>
            </div>
          </a>

          <div className="p-5 rounded-2xl border border-white/10 bg-[#13131A]/60">
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 mb-3" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              OTHER DOCUMENTS
            </div>
            <div className="flex flex-col gap-1.5">
              {otherLinks.map((l) => (
                <Link
                  key={l.slug}
                  to={`/legal/${l.slug}`}
                  data-testid={`legal-related-${l.slug}`}
                  className="text-sm text-zinc-300 hover:text-[#7DD3FC] transition-colors"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  → {l.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
